import { cn } from '@/lib/utils'
import type { SheetConnector, SheetConnectorEdge } from '@/types/sheet-connector'
import { Plus, AlertTriangle } from 'lucide-react'
import { useTranslation } from '@/i18n'

type Side = 'left' | 'right' | 'top' | 'bottom'

interface SheetEdgeNavProps {
  currentIndex: number
  totalSheets: number
  sheetNames?: string[]
  onNavigate: (index: number) => void
  connectors?: SheetConnector[]
  multiPageDocs?: { id: string; name: string }[]
  onAddConnector?: (edge: SheetConnectorEdge) => void
  onConnectorClick?: (connector: SheetConnector) => void
  onConnectorContextMenu?: (e: React.MouseEvent, connector: SheetConnector) => void
  /**
   * Vertical offset for the TOP connector lane. The V2 sheet page bar floats in
   * that same top-centre band, so its host pushes this lane below it; left at
   * `top-12` otherwise.
   */
  topOffsetClass?: string
}

const EDGE_ARROW: Record<Side, string> = {
  left: '\u2190',
  right: '\u2192',
  top: '\u2191',
  bottom: '\u2193',
}

function ConnBtn({
  side,
  connector,
  targetLabel,
  onClick,
  onContextMenu,
}: {
  side: Side
  connector: SheetConnector
  targetLabel: string
  onClick: () => void
  onContextMenu?: (e: React.MouseEvent) => void
}) {
  const isOut = connector.direction === 'out'
  const isLinked = !!connector.target_document_id
  const arrow = EDGE_ARROW[side]

  return (
    <button
      onClick={onClick}
      onContextMenu={onContextMenu}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium whitespace-nowrap',
        'transition-all duration-150 cursor-pointer',
        'hover:shadow-md hover:brightness-95',
        isOut
          ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-800 text-amber-700 dark:text-amber-400'
          : 'bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-800 text-blue-700 dark:text-blue-400',
        !isLinked && 'opacity-50',
      )}
    >
      {!isOut && <span className="font-bold text-sm">{arrow}</span>}
      <span className="font-bold text-xs">{connector.connector_id}</span>
      {targetLabel && (
        <span className="text-[10px] opacity-50 max-w-[120px] truncate">{targetLabel}</span>
      )}
      {connector.label && (
        <span className="text-[10px] opacity-75 max-w-[80px] truncate">{connector.label}</span>
      )}
      {isOut && <span className="font-bold text-sm">{arrow}</span>}
    </button>
  )
}

function AddBtn({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation()
  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center justify-center rounded-md border border-dashed border-gray-300 dark:border-gray-600',
        'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500 transition-colors cursor-pointer',
        'w-7 h-7',
      )}
      title={t('graph_editor:sheetNav.addConnector', { defaultValue: 'Add connector' })}
    >
      <Plus size={14} />
    </button>
  )
}

function EdgeBar({
  side,
  connectors,
  multiPageDocs,
  onConnectorClick,
  onConnectorContextMenu,
  onAdd,
}: {
  side: Side
  connectors: SheetConnector[]
  multiPageDocs?: { id: string; name: string }[]
  onConnectorClick?: (connector: SheetConnector) => void
  onConnectorContextMenu?: (e: React.MouseEvent, connector: SheetConnector) => void
  onAdd?: () => void
}) {
  const { t } = useTranslation()
  const isVert = side === 'left' || side === 'right'
  const hasContent = connectors.length > 0 || onAdd

  if (!hasContent) return null

  return (
    <div
      className={cn(
        'flex justify-center items-center gap-1.5 shrink-0',
        isVert ? 'flex-col px-1.5' : 'flex-row py-1',
      )}
    >
      {connectors.length >= 4 && (
        <span className="inline-flex items-center gap-1 text-[10px] text-orange-500" title={t('graph_editor:sheetNav.edgeCrowded', { defaultValue: 'This edge is getting crowded' })}>
          <AlertTriangle size={12} />
        </span>
      )}
      {connectors.map((c) => {
        const targetDoc = c.target_document_id
          ? multiPageDocs?.find((d) => d.id === c.target_document_id)
          : undefined
        return (
          <ConnBtn
            key={c.id}
            side={side}
            connector={c}
            targetLabel={targetDoc?.name ?? ''}
            onClick={() => onConnectorClick?.(c)}
            onContextMenu={onConnectorContextMenu ? (e) => onConnectorContextMenu(e, c) : undefined}
          />
        )
      })}
      {onAdd && <AddBtn onClick={onAdd} />}
    </div>
  )
}

/**
 * Renders connector-style navigation buttons at the edges of the P&ID
 * diagram. When connectors data is provided, renders real per-edge
 * connectors; otherwise falls back to prev/next sequential navigation.
 */
export default function SheetEdgeNav({
  currentIndex,
  totalSheets,
  sheetNames,
  onNavigate,
  connectors,
  multiPageDocs,
  onAddConnector,
  onConnectorClick,
  onConnectorContextMenu,
  topOffsetClass = 'top-12',
}: SheetEdgeNavProps) {
  const { t } = useTranslation()
  if (totalSheets <= 1 && (!connectors || connectors.length === 0)) return null

  // If we have real connector data, render data-driven view
  if (connectors) {
    const byEdge = (edge: SheetConnectorEdge) =>
      connectors.filter((c) => c.edge === edge)

    const handleClick = (connector: SheetConnector) => {
      if (onConnectorClick) {
        onConnectorClick(connector)
        return
      }
      // Fallback: navigate to target document
      if (connector.target_document_id && multiPageDocs) {
        const idx = multiPageDocs.findIndex((d) => d.id === connector.target_document_id)
        if (idx >= 0) onNavigate(idx)
      }
    }

    const edges: Side[] = ['top', 'bottom', 'left', 'right']
    const positionStyles: Record<Side, string> = {
      top: `absolute ${topOffsetClass} left-0 right-0 z-20 flex justify-center pointer-events-none`,
      bottom: 'absolute bottom-0 left-0 right-0 z-20 flex justify-center pointer-events-none',
      left: 'absolute left-0 top-0 bottom-0 z-20 flex items-center pointer-events-none',
      right: 'absolute right-0 top-0 bottom-0 z-20 flex items-center pointer-events-none',
    }

    return (
      <>
        {edges.map((side) => {
          const edgeConnectors = byEdge(side)
          const hasAdd = !!onAddConnector
          if (edgeConnectors.length === 0 && !hasAdd) return null
          return (
            <div key={side} className={positionStyles[side]}>
              <div className="pointer-events-auto">
                <EdgeBar
                  side={side}
                  connectors={edgeConnectors}
                  multiPageDocs={multiPageDocs}
                  onConnectorClick={handleClick}
                  onConnectorContextMenu={onConnectorContextMenu}
                  onAdd={onAddConnector ? () => onAddConnector(side) : undefined}
                />
              </div>
            </div>
          )
        })}
      </>
    )
  }

  // Fallback: prev/next sequential navigation
  const hasPrev = currentIndex > 0
  const hasNext = currentIndex < totalSheets - 1

  const prevButtons = hasPrev
    ? [
        {
          sheetIndex: currentIndex - 1,
          label: sheetNames?.[currentIndex - 1] ?? t('graph_editor:sheetNav.sheet', { count: currentIndex, defaultValue: 'Sheet {{count}}' }),
          onClick: () => onNavigate(currentIndex - 1),
        },
      ]
    : []

  const nextButtons = hasNext
    ? [
        {
          sheetIndex: currentIndex + 1,
          label: sheetNames?.[currentIndex + 1] ?? t('graph_editor:sheetNav.sheet', { count: currentIndex + 2, defaultValue: 'Sheet {{count}}' }),
          onClick: () => onNavigate(currentIndex + 1),
        },
      ]
    : []

  return (
    <>
      {hasPrev && (
        <>
          <div className={`absolute ${topOffsetClass} left-0 right-0 z-20 flex justify-center pointer-events-none`}>
            <div className="pointer-events-auto">
              <FallbackEdgeBar side="top" buttons={prevButtons} />
            </div>
          </div>
          <div className="absolute left-0 top-0 bottom-0 z-20 flex items-center pointer-events-none">
            <div className="pointer-events-auto">
              <FallbackEdgeBar side="left" buttons={prevButtons} />
            </div>
          </div>
        </>
      )}
      {hasNext && (
        <>
          <div className="absolute bottom-0 left-0 right-0 z-20 flex justify-center pointer-events-none">
            <div className="pointer-events-auto">
              <FallbackEdgeBar side="bottom" buttons={nextButtons} />
            </div>
          </div>
          <div className="absolute right-0 top-0 bottom-0 z-20 flex items-center pointer-events-none">
            <div className="pointer-events-auto">
              <FallbackEdgeBar side="right" buttons={nextButtons} />
            </div>
          </div>
        </>
      )}
    </>
  )
}

// Legacy fallback button for sequential prev/next navigation
function FallbackConnBtn({
  side,
  sheetIndex,
  label,
  onClick,
}: {
  side: Side
  sheetIndex: number
  label: string
  onClick: () => void
}) {
  const isForward = side === 'right' || side === 'bottom'
  const arrow = EDGE_ARROW[side]
  const badgeLetter = String.fromCharCode(65 + (sheetIndex % 26))

  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium whitespace-nowrap',
        'transition-all duration-150 cursor-pointer',
        'hover:shadow-md hover:brightness-95',
        isForward
          ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-800 text-amber-700 dark:text-amber-400'
          : 'bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-800 text-blue-700 dark:text-blue-400',
      )}
    >
      {!isForward && <span className="font-bold text-sm">{arrow}</span>}
      <span className="font-bold text-xs">{badgeLetter}</span>
      <span className="text-[10px] opacity-75">P{String(sheetIndex + 1).padStart(2, '0')}</span>
      <span className="text-[10px] opacity-50 max-w-[120px] truncate">{label}</span>
      {isForward && <span className="font-bold text-sm">{arrow}</span>}
    </button>
  )
}

function FallbackEdgeBar({
  side,
  buttons,
}: {
  side: Side
  buttons: { sheetIndex: number; label: string; onClick: () => void }[]
}) {
  const isVert = side === 'left' || side === 'right'
  if (buttons.length === 0) return null

  return (
    <div
      className={cn(
        'flex justify-center items-center gap-1.5 shrink-0',
        isVert ? 'flex-col px-1.5' : 'flex-row py-1',
      )}
    >
      {buttons.map((b) => (
        <FallbackConnBtn
          key={b.sheetIndex}
          side={side}
          sheetIndex={b.sheetIndex}
          label={b.label}
          onClick={b.onClick}
        />
      ))}
    </div>
  )
}
